import {Alert, Box, Button, Modal, Typography} from '@mui/material';
import React, {useCallback, useState} from 'react';
import styles from '../../styles';
import {useTranslation} from 'react-i18next';
import {createCustomerPortalSession} from '../../api/membershipAPI';
import {updateProfileMembershipAsync} from '../../store/profileSlice';
import {useAppDispatch} from '../../store/hooks';
import {Profile} from '../../interfaces';
import CancelMembershipReasonForm, {
  CancelMembershipReasonFormData,
  getCancelMembershipReasons,
} from './CancelMembershipReasonForm';
import {getMembershipLevelName} from '../../services/membershipService';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  membershipLevel: string;
  updateMembershipLevelError: string;
  setUpdateMembershipLevelError: (error: string) => void;
  profile: Profile;
  profileIdInt?: number;
}

const StripeMembershipChangeModal: React.FC<Props> = ({
  open,
  setOpen,
  membershipLevel,
  updateMembershipLevelError,
  setUpdateMembershipLevelError,
  profile,
  profileIdInt,
}) => {
  const {t} = useTranslation();
  const [step, setStep] = useState<'info' | 'reason'>('info');
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    setOpen(false);
    setStep('info');
  }, [setOpen]);

  const handleCancelMembership = useCallback(
    async (data: CancelMembershipReasonFormData) => {
      const {reasons, additionalNotes} = getCancelMembershipReasons(data);

      const res = await dispatch(
        updateProfileMembershipAsync({
          profileId: profileIdInt || undefined,
          membershipLevel,
          reasons,
          additionalNotes,
        }),
      );
      if (res?.payload?.success && res?.payload?.url) {
        window.location = res.payload.url;
      }
      if (res?.payload?.data?.error) {
        setUpdateMembershipLevelError(res?.payload?.data?.error);
      }
    },
    [dispatch, membershipLevel, profileIdInt, setUpdateMembershipLevelError],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={styles.modal}>
        <Box sx={{display: 'flex', flexDirection: 'column'}}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t(
              membershipLevel === 'rec'
                ? 'membership.cancelMembership'
                : 'membership.updateMembershipLevel',
            )}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
            }}>
            {membershipLevel === 'rec'
              ? t('membership.cancelMembershipStripeInstructions')
              : t('membership.updateMembershipLevelInstructions', {
                  plan: getMembershipLevelName(membershipLevel),
                })}
          </Typography>
          {updateMembershipLevelError ? (
            <Alert severity="error" variant="outlined" sx={{mb: 1}}>
              {updateMembershipLevelError}
            </Alert>
          ) : null}
          {step === 'info' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
              }}>
              <Button
                variant={membershipLevel === 'rec' ? 'outlined' : 'contained'}
                sx={{mb: 1, mr: 1}}
                onClick={async () => {
                  if (membershipLevel === 'rec') {
                    setStep('reason');
                  } else {
                    const res = await dispatch(
                      updateProfileMembershipAsync({
                        profileId: profileIdInt || undefined,
                        membershipLevel,
                      }),
                    );
                    if (res?.payload?.success && res?.payload?.url) {
                      window.location = res.payload.url;
                    }
                    if (res?.payload?.data?.error) {
                      setUpdateMembershipLevelError(res?.payload?.data?.error);
                    }
                  }
                }}>
                {membershipLevel === 'rec'
                  ? t('general.continue')
                  : t('general.confirm')}
              </Button>
              {profile.stripeCustomerId && membershipLevel !== 'rec' ? (
                <Button
                  variant="outlined"
                  sx={{mb: 1, mr: 1}}
                  onClick={async () => {
                    const res = await createCustomerPortalSession({
                      profileId: profile.id as number,
                    });
                    if (res?.url) {
                      window.location.href = res.url;
                    } else {
                      window.alert(t('general.somethingWentWrong'));
                    }
                  }}>
                  {t('membership.updatePaymentInformation')}
                </Button>
              ) : null}
              <Button
                variant={membershipLevel === 'rec' ? 'contained' : 'outlined'}
                sx={{mb: 1}}
                onClick={handleClose}>
                {t('general.close')}
              </Button>
            </Box>
          )}
          {step === 'reason' && (
            <CancelMembershipReasonForm
              onSubmit={handleCancelMembership}
              onClose={handleClose}
              // loading={pending}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default StripeMembershipChangeModal;
